import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard, Car, Wrench, DollarSign, Megaphone, Box,
  Headphones, Settings, LogOut, ChevronLeft, Menu, Bell, Search, BarChart3, Warehouse, Link2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  { icon: Car, label: "Sales", path: "/sales" },
  { icon: Warehouse, label: "Inventory", path: "/inventory" },
  { icon: Wrench, label: "Service", path: "/service" },
  { icon: DollarSign, label: "F&I", path: "/fi" },
  { icon: Megaphone, label: "Marketing", path: "/marketing" },
  { icon: Box, label: "Parts", path: "/parts" },
  { icon: Link2, label: "Integrations", path: "/integrations" },
  { icon: Headphones, label: "Support", path: "/support" },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <button
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-sm bg-[#1D1D1B] text-white"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      <aside className={`portal-sidebar ${mobileOpen ? 'open' : ''} ${collapsed ? 'w-16' : 'w-64'}`} data-testid="sidebar">
        {/* Logo */}
        <div className={`p-4 border-b border-white/10 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-white text-lg">HalcyonAuto</span>
            </Link>
          )}
          {collapsed && (
            <Link to="/dashboard">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
            </Link>
          )}
          {!collapsed && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:flex p-1.5 rounded-sm hover:bg-white/10 text-slate-400"
              data-testid="collapse-btn"
            >
              <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
          
          {user?.role === "admin" && (
            <>
              <div className={`py-2 ${collapsed ? '' : 'px-4'}`}>
                <div className="border-t border-white/10"></div>
              </div>
              <Link
                to="/admin"
                className={`sidebar-item ${location.pathname === '/admin' ? 'active' : ''}`}
                data-testid="nav-admin"
              >
                <BarChart3 className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">Admin</span>}
              </Link>
            </>
          )}
        </nav>

        {/* User */}
        <div className={`p-3 border-t border-white/10 ${collapsed ? 'px-2' : ''}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`w-full flex items-center gap-3 p-2 rounded-sm hover:bg-white/10 ${collapsed ? 'justify-center' : ''}`} data-testid="user-menu">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">{user?.name?.charAt(0) || 'U'}</span>
                </div>
                {!collapsed && (
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-white truncate">{user?.name}</div>
                    <div className="text-xs text-slate-400 truncate">{user?.role || user?.email}</div>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem data-testid="menu-settings">
                <Settings className="w-4 h-4 mr-2" />Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" />Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </>
  );
};

export const Header = ({ title, subtitle }) => {
  return (
    <header className="portal-header">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground uppercase" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-[#FF4D00] rounded-full"></span>
        </Button>
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search..."
            className="input-allplan pl-10 w-64"
            data-testid="header-search"
          />
        </div>
      </div>
    </header>
  );
};

export default Sidebar;
